import React, { useState } from "react";
import { useReveal } from "@/lib/useReveal";
import { ExternalLink, Bookmark } from "lucide-react";
import { SocialLogo } from "@/components/landing/SocialLogos";

/**
 * "How Leadmato reads the room" - redesigned as a single editorial card that
 * tells the story vertically. Big platform logo + raw quote at the top,
 * a graphical scoring strip in the middle (radial gauge + progress bar +
 * keyword pills), and the AI-drafted reply at the bottom. Less text, more
 * visual weight, no arrow indicator needed - the layout itself is the flow.
 */

const EXAMPLES = [
  {
    id: "d2c",
    category: "D2C · Skincare",
    accent: "#D95A40",
    accentSoft: "#F5A48A",
    screenshot: "/reddit/d2c.png",
    subreddit: "r/30PlusSkinCare",
    signal: {
      score: 94,
      intent: "Buying",
      persona_fit: 92,
      matched_keywords: ["olive skin tone", "no white cast", "under $20", "affordable sunscreen"],
    },
    reply:
      "Been there — the white cast on olive skin is real. Two things helped me: 1) go for tinted mineral formulas (they neutralize the cast on warmer undertones), 2) look for formulas listing 'nano zinc' rather than plain zinc oxide. Both criteria hit under-$20 pretty easily. Happy to share the two brands I rotate if you want to DM.",
    tone: "Friendly",
  },
  {
    id: "saas",
    category: "B2B · SaaS",
    accent: "#1F4A38",
    accentSoft: "#9DC183",
    screenshot: "/reddit/saas.png",
    subreddit: "r/Salesforce",
    signal: {
      score: 96,
      intent: "Buying",
      persona_fit: 95,
      matched_keywords: ["AI-driven CRM", "Salesforce alternatives", "cost of services", "modernization"],
    },
    reply:
      "Your framing is exactly right — the 'Salesforce tax' on internal business-system use is where AI-first CRMs are winning. For a mature company, the migration risk is real, so I'd suggest running a parallel pilot on one service line before ripping anything out. Happy to share how three peers structured their 90-day pilots — DM me if useful.",
    tone: "Helpful",
  },
  {
    id: "local",
    category: "Local · Dentistry",
    accent: "#B8813A",
    accentSoft: "#E0A85A",
    screenshot: "/reddit/local.png",
    subreddit: "r/Bengaluru",
    signal: {
      score: 95,
      intent: "Buying",
      persona_fit: 94,
      matched_keywords: ["root canal", "HSR / Koramangala", "personal experience", "Bengaluru"],
    },
    reply:
      "Hey! I got my RCT done at a clinic in HSR Sector 6 last month — fixed transparent quote (₹6-9k depending on tooth), the dentist walked me through the X-ray before starting, and there was zero upsell pressure on crowns. DM if you'd like the doctor's name and consultation number.",
    tone: "Friendly",
  },
  {
    id: "edtech",
    category: "Ed-Tech · Coaches",
    accent: "#7A3EA5",
    accentSoft: "#B48ADB",
    screenshot: "/reddit/edtech.png",
    subreddit: "r/datascience",
    signal: {
      score: 89,
      intent: "Research",
      persona_fit: 90,
      matched_keywords: ["Data Science career", "AI tools evolving", "which skills matter", "2026"],
    },
    reply:
      "Honest take from someone hiring DS folks in 2026: the pure 'notebook + XGBoost' role is fading, but demand for people who can pair statistical rigor with LLM/eval tooling is exploding. Skills that matter more today: causal inference, MLOps, prompt+eval design. Our Jan cohort restructured entirely around this stack — happy to share the syllabus if useful.",
    tone: "Expert",
  },
];

export default function RealConversations() {
  const [head, headVisible] = useReveal();
  const [active, setActive] = useState(0);
  const ex = EXAMPLES[active];

  return (
    <section id="how" className="relative lm-cv py-20 md:py-28 bg-lm-bg overflow-hidden">
      <div className="max-w-6xl mx-auto px-5 md:px-10">
        {/* Header */}
        <div
          ref={head}
          className={`max-w-3xl mb-10 md:mb-12 lm-reveal ${headVisible ? "is-visible" : ""}`}
        >
          <div className="text-xs font-mono uppercase tracking-widest text-lm-secondary mb-3">
            How Leadmato reads the room
          </div>
          <h2 className="text-3xl md:text-5xl font-display font-semibold leading-[1.08] tracking-tight text-lm-text">
            Real conversations. Ranked, scored, and saved.
          </h2>
        </div>

        {/* Category selector */}
        <div className="flex flex-wrap gap-2 mb-8">
          {EXAMPLES.map((e, i) => (
            <button
              key={e.id}
              data-testid={`real-conv-tab-${e.id}`}
              onClick={() => setActive(i)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                active === i
                  ? "text-white shadow-[0_6px_18px_-6px_rgba(0,0,0,0.25)]"
                  : "bg-lm-surface text-lm-text2 border-lm-border hover:border-lm-text/40 hover:text-lm-text"
              }`}
              style={
                active === i
                  ? { backgroundColor: e.accent, borderColor: e.accent }
                  : {}
              }
            >
              {e.category}
            </button>
          ))}
        </div>

        {/* Unified editorial card */}
        <div
          data-testid="signal-card"
          className="relative bg-lm-surface border border-lm-border rounded-2xl overflow-hidden"
          style={{ boxShadow: `0 30px 80px -30px ${ex.accent}25` }}
        >

          <div className="relative grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_1px_minmax(0,1fr)]">
            {/* LEFT: Real Reddit screenshot */}
            <div className="p-4 md:p-6 bg-[#0B0F17] flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <SocialLogo platform="Reddit" className="w-6 h-6 shrink-0" />
                <div className="text-xs font-mono uppercase tracking-widest text-white/60">
                  {ex.subreddit} · captured from Reddit
                </div>
                <div className="ml-auto text-[10px] font-mono uppercase tracking-widest text-white/40 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-pulse" />
                  raw
                </div>
              </div>
              <div
                className="relative rounded-lg overflow-hidden bg-black flex-1 flex items-center justify-center min-h-[380px] md:min-h-[420px]"
                data-testid="raw-post-card"
              >
                {EXAMPLES.map((e, i) => (
                  <img
                    key={e.id}
                    src={e.screenshot}
                    alt={`Reddit post from ${e.subreddit} — illustrative signal`}
                    className={`absolute inset-0 m-auto w-full h-auto max-h-[420px] md:max-h-[460px] object-contain block transition-opacity duration-200 ${
                      i === active ? "opacity-100" : "opacity-0 pointer-events-none"
                    }`}
                    draggable="false"
                    fetchpriority={i === 0 ? "high" : "low"}
                    aria-hidden={i !== active}
                  />
                ))}
              </div>
            </div>

            {/* DIVIDER */}
            <div className="h-px lg:h-auto lg:w-px bg-lm-border" aria-hidden />

            {/* RIGHT: Leadmato's analysis */}
            <div className="p-6 md:p-8 flex flex-col gap-5">
              {/* Score row: big radial gauge + intent + persona fit */}
              <div className="flex items-center gap-5">
                <ScoreRing score={ex.signal.score} accent={ex.accent} />
                <div className="flex-1 min-w-0">
                  <div
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider font-semibold mb-3"
                    style={{
                      backgroundColor: `${ex.accent}15`,
                      color: ex.accent,
                    }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: ex.accent }}
                    />
                    {ex.signal.intent} intent
                  </div>

                  <div>
                    <div className="flex items-baseline justify-between text-[10px] font-mono uppercase tracking-widest text-lm-text3 mb-1">
                      <span>Persona fit</span>
                      <span className="font-semibold text-lm-text2 tabular-nums">
                        {ex.signal.persona_fit}
                      </span>
                    </div>
                    <div className="h-1.5 bg-lm-tint rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-[width] duration-700"
                        style={{
                          width: `${ex.signal.persona_fit}%`,
                          background: `linear-gradient(90deg, ${ex.accent}, ${ex.accentSoft})`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Keywords */}
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3 mb-2">
                  Signals surfaced
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {ex.signal.matched_keywords.map((k) => (
                    <span
                      key={k}
                      className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-mono border"
                      style={{
                        backgroundColor: `${ex.accent}0d`,
                        borderColor: `${ex.accent}25`,
                        color: ex.accent,
                      }}
                    >
                      {k}
                    </span>
                  ))}
                </div>
              </div>

              {/* Lead actions — mirrors the real product card */}
              <div className="mt-auto flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-lm-border bg-lm-surface text-sm font-medium text-lm-text2">
                  <ExternalLink className="w-3.5 h-3.5" /> Open source
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-lm-primary text-white text-sm font-medium">
                  <Bookmark className="w-3.5 h-3.5" /> Save lead
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Circular score gauge - rendered as a stroked SVG ring. The colored arc length
 * corresponds to the score / 100. Number rendered inside.
 */
function ScoreRing({ score, accent }) {
  const size = 82;
  const stroke = 7;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.min(Math.max(score, 0), 100) / 100);

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#EEE7DC"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={accent}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.9s cubic-bezier(0.22, 1, 0.36, 1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div
          className="text-2xl md:text-[26px] font-display font-bold leading-none tabular-nums"
          style={{ color: accent }}
        >
          {score}
        </div>
        <div className="text-[9px] font-mono uppercase tracking-widest text-lm-text3 mt-0.5">
          score
        </div>
      </div>
    </div>
  );
}
